import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { LoadingService } from '@core/loading/loading.service';

/**
 * Barre de progression globale (style GitHub/YouTube) : fine bande animée
 * en haut de l'écran, non bloquante. S'affiche automatiquement dès que le
 * LoadingService est actif (transitions de route, requêtes HTTP).
 *
 * À placer une seule fois, au niveau du composant racine, au-dessus du
 * <router-outlet /> pour couvrir aussi bien le login que les pages internes.
 */
@Component({
  selector: 'idra-loading-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      position: fixed;
      inset: 0 0 auto 0;
      z-index: 2000;
      height: 3px;
      pointer-events: none;
    }

    .bar {
      position: absolute;
      inset: 0;
      overflow: hidden;
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    :host(.active) .bar {
      opacity: 1;
    }

    /* Deux traînées décalées : effet « indéterminé » fluide. */
    .bar::before,
    .bar::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      background: var(--idra-primary);
      will-change: transform;
    }

    .bar::before {
      animation: idra-loading-a 2.1s cubic-bezier(0.65, 0.05, 0.36, 1) infinite;
    }

    .bar::after {
      animation: idra-loading-b 2.1s cubic-bezier(0.16, 0.84, 0.44, 1) infinite;
    }

    @keyframes idra-loading-a {
      0% {
        transform: translateX(-100%) scaleX(0.2);
      }
      60%,
      100% {
        transform: translateX(100%) scaleX(0.6);
      }
    }

    @keyframes idra-loading-b {
      0%,
      50% {
        transform: translateX(-100%) scaleX(0.1);
      }
      100% {
        transform: translateX(100%) scaleX(0.5);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .bar::before,
      .bar::after {
        animation-duration: 3.5s;
      }
    }
  `,
  template: `<div
    class="bar"
    role="progressbar"
    aria-label="Chargement en cours"
    [attr.aria-hidden]="loading.active() ? null : true"
  ></div>`,
  host: {
    '[class.active]': 'loading.active()',
    '[attr.aria-busy]': 'loading.active()',
  },
})
export class IdraLoadingBar {
  protected readonly loading = inject(LoadingService);
}
